import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { AllLabels, InstallationFlowType } from '../../../constants/app.constants';
import { FormTypesData, KeyValueData, TechnicianData } from '../../../domains/common.domain';
import { TechnicianDataService } from '../technician-data.service';
import { LookupOptionsData } from '../../jurisdiction.domain';
import { CommunicationService } from '../../../services/communication.service';
import { AllDropdownKeys, UrlSegments } from '../../jurisdiction.constants';
import { ErrorService } from '../../../services/error/error.service';
import * as _ from 'lodash';
import { EnvironmentService } from '../../../services/environment.service';
import { CommonService } from '../../../services/common.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { ValidationsService } from '../../../services/validations.service';
import { DestroyableComponent } from '../../../common/destroyable.component';
import { debounceTime, filter } from 'rxjs/operators';
import { UserService } from '../../../services/user.service';
import { SectionIdentifier, SectionStatus } from '../../../+shared/components/review-section/review-section.domain';

@Component({
  selector: 'app-power-connection',
  styleUrls: ['./power-connection.component.scss'],
  templateUrl: './power-connection.component.html',
})
export class TechnicianPowerConnectionComponent extends DestroyableComponent implements OnChanges, OnInit, AfterViewInit {
  @Input() currentFormType: FormTypesData;
  @Input() powerConnectionData: any = null;
  @Input() isReadOnly: boolean = false;
  @Input() isDetailsView: boolean = false;
  @Input() applicationId: boolean = false;
  @Input() applicationStatus: string = undefined;

  @Output() onPowerConnectionReviewSet = new EventEmitter<SectionStatus>(); // related to https://github.com/bittner-krull/gop-web/issues/2062
  @Output() onPowerConnectionLocationChange = new EventEmitter<string>();

  currentFlowType: string = this.localStorageService.getItem('installation_flow');
  infoBubblesData: {
    [key: string]: boolean;
  } = this.environmentService.getInfoBubblesData('technicianData') || null;
  powerConnectionFG: FormGroup;
  requiredFusePowerTypeKey: string = AllDropdownKeys.SWKNRequiredFusePower;
  requiredFusePowerTypes: LookupOptionsData[] = this.commonService.getDropdownData(AllDropdownKeys.SWKNRequiredFusePower);
  installationLocationTypes: LookupOptionsData[] = this.commonService.getDropdownData(AllDropdownKeys.PowerConnectionLocation);
  powerConnectionHeatingTypes: LookupOptionsData[] = this.commonService.getDropdownData(AllDropdownKeys.PowerConnectionHeatingType);
  showAddPowerDeviceButton: boolean = this.environmentService.addPowerDevice;
  showConnectedLoadLabel: boolean = this.environmentService.showConnectedLoadLabel;
  showStandardPowerWarning: boolean = this.environmentService.showStandardPowerWarning;
  showInfoForCommodities: boolean = this.environmentService.showInfoForCommodities;
  specialDeviceCount: number[] = [];
  specialDeviceData: any[];
  unitLabels: KeyValueData = AllLabels.units;
  usePowerConnectionExtendedOptionsForNewMains = this.environmentService.usePowerConnectionExtendedOptionsForNewMains;
  showSpecialConsumers = this.environmentService.showSpecialConsumers;
  technicianName = null;
  sectionIdentifier = SectionIdentifier;
  earthworkAreaKey: string = AllDropdownKeys.EarthworkAreaName;
  earthworkAreaTypes: LookupOptionsData[];

  get isCreatedStatus(): boolean {
    return this.applicationStatus === 'created';
  }

  get isDraftStatus(): boolean {
    return this.applicationStatus === 'draft';
  }

  get isDraftRejectedStatus(): boolean {
    return this.applicationStatus === 'draftRejected';
  }

  constructor(
    private commonService: CommonService,
    private communicationService: CommunicationService,
    private errorService: ErrorService,
    private technicianDataService: TechnicianDataService,
    public environmentService: EnvironmentService,
    private localStorageService: LocalStorageService,
    private cdr: ChangeDetectorRef,
    private userService: UserService
  ) {
    super();
  }

  ngOnInit(): void {
    this.prepareForm();
    this.handleChildForms();

    this.preventLeak(this.technicianDataService.registerForActionOnFormSubmit)
      .pipe(filter(value => !!value))
      .subscribe(() => {
        this.emitFormData();
        this.resetData();
      });
    if (this.currentFlowType === InstallationFlowType.MainsConnection) {
      this.showAddPowerDeviceButton = this.showAddPowerDeviceButton && !this.environmentService.hidePowerDeviceAddForNewMains;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes && changes.currentFormType && !changes.currentFormType.firstChange) ||
      (changes &&
        changes.powerConnectionData &&
        !changes.powerConnectionData.firstChange &&
        !_.isEqual(changes.powerConnectionData.previousValue, changes.powerConnectionData.currentValue))
    ) {
      this.specialDeviceCount = [];
      this.specialDeviceData = [];
      this.technicianDataService.manageSpecialConsumersFormData({
        component: 'specialDevice',
        formData: null,
        index: -1,
      });
      this.prepareForm();
    }
  }

  ngAfterViewInit(): void {
    if (!this.showTempConnectionRegistrationForNewMains) return;

    const control = this.powerConnectionFG.get('requiredTempConnectionLess30');
    if (!control) return;

    if (this.isCreatedStatus || this.isDraftRejectedStatus) {
      control.enable({ emitEvent: false });
    } else if (this.isDraftStatus) {
      control.disable({ emitEvent: false });
    }
  }

  get powerConnectionHeaderTranslationKey(): string {
    if (this.currentFlowType === InstallationFlowType.MainsConnection) {
      return 'technician-data.power-connection-title-new-mains';
    }

    return 'technician-data.power-connection-title';
  }

  get shouldShowWarningInfo(): boolean {
    return (
      this.currentFlowType == InstallationFlowType.MainsConnection &&
      this.environmentService.usePowerConnectionTypeForNewMains &&
      this.isDetailsView &&
      this.powerConnectionFG.get('installationLocationType')?.dirty
    );
  }

  handleChildForms(): void {
    this.preventLeak(this.technicianDataService.registerForSpecialConsumersUpdate).subscribe(data =>
      (this.powerConnectionFG.controls.specialDevice as FormArray).push(data.formData)
    );
  }

  prepareData(): void {
    this.specialDeviceData = (this.powerConnectionData && this.powerConnectionData.specialDevice) || [];
    this.specialDeviceCount = [...Array(this.specialDeviceData.length).keys()];
  }

  addSpecialDevice(): void {
    this.specialDeviceCount.push(this.specialDeviceCount.length ? this.specialDeviceCount[this.specialDeviceCount.length - 1] + 1 : 0);
  }

  removeChildElements(index: number): void {
    let currentIndex = 0;
    const elementName = this.currentFormType.childElement;

    this[`${elementName}Count`].forEach((item, i) => {
      if (item === index) {
        currentIndex = i;
      }
    });

    this[`${elementName}Count`].splice(currentIndex, 1);
    (this.powerConnectionFG.controls[elementName] as FormArray).controls.splice(currentIndex, 1);
    (this.powerConnectionFG.controls[elementName] as FormArray).updateValueAndValidity();

    if (this.specialDeviceData.length && this.specialDeviceData[currentIndex]) {
      this.preventLeak(
        this.communicationService.deleteData(`${UrlSegments.UpdatePowerGridSpecialConsumer}${this.specialDeviceData[currentIndex].id}`)
      ).subscribe(
        response => {
          this.errorService.throwErrorIfFailure(response, true);
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    }
  }

  isFieldRequired(field: string) {
    const formField = this.powerConnectionFG.get(field);
    return ValidationsService.isFieldRequired(formField);
  }

  get isTotalOutputReadOnly(): boolean {
    return (
      this.environmentService.usePowerConnectionTypeForNewMains &&
      this.powerConnectionData?.sk_power_connection_type === 'standard_mains_connection' &&
      this.currentFlowType == InstallationFlowType.MainsConnection
    );
  }

  private prepareForm(): void {
    this.powerConnectionFG = new FormGroup({
      id: new FormControl((this.powerConnectionData && this.powerConnectionData.id) || ''),
      specialDevice: new FormArray([]),
    });

    if (
      this.environmentService.showTotalOutputField ||
      (this.currentFlowType !== InstallationFlowType.MainsConnection && this.currentFlowType !== InstallationFlowType.ChangeHouseConnection)
    ) {
      this.powerConnectionFG.addControl(
        'totalOutput',
        new FormControl(
          (this.powerConnectionData && this.powerConnectionData.totalOutput) || this.environmentService.defaultTotalPowerOutput,
          this.environmentService.totalOutputRequired ? [Validators.required] : null
        )
      );
    }

    if (
      this.environmentService.showFuseStrengthDropdown &&
      (this.currentFlowType === InstallationFlowType.MainsConnection || this.currentFlowType === InstallationFlowType.ChangeHouseConnection)
    ) {
      this.powerConnectionFG.addControl(
        'requiredFusePower',
        new FormControl((this.powerConnectionData && this.powerConnectionData.requiredFusePower) || '', [Validators.required])
      );
    }

    if (this.environmentService.powerConnectionShowEquipmentAvailableCheckboxes) {
      this.powerConnectionFG.addControl('craneAvailable', new FormControl(false, [Validators.required]));
      this.powerConnectionFG.addControl('pumpAvailable', new FormControl(false, [Validators.required]));
    }

    if (this.showAddPowerDeviceButton) {
      this.prepareData();
    }

    if (this.showPowerConnectionExtendendOptionsForNewMains) {
      if (this.showPowerHouseConnectionLocationOptionsForNewMains) {
        this.powerConnectionFG.addControl(
          'installationLocationType',
          new FormControl(this.powerConnectionData?.installationLocationType, [Validators.required])
        );
        this.powerConnectionFG.addControl(
          'installationLocationTypeOther',
          new FormControl(this.powerConnectionData?.installationLocationTypeOther, [])
        );

        this.powerConnectionFG.controls.installationLocationType.valueChanges.subscribe(value => {
          if (value.toLowerCase() !== 'other') {
            this.powerConnectionFG.controls.installationLocationTypeOther.setValue('');
            this.powerConnectionFG.controls.installationLocationTypeOther.clearValidators();
          } else {
            this.powerConnectionFG.controls.installationLocationTypeOther.setValidators([Validators.required]);
          }
          this.powerConnectionFG.controls.installationLocationTypeOther.updateValueAndValidity();
        });
      }
      this.powerConnectionFG.addControl(
        'offerEarthworksOnPrivateProperty',
        new FormControl(this.powerConnectionData?.offerEarthworksOnPrivateProperty, [Validators.required])
      );
      this.powerConnectionFG.addControl(
        'powerConnectionHeatingType',
        new FormControl(this.powerConnectionData?.powerConnectionHeatingType, [Validators.required])
      );
      this.powerConnectionFG.addControl(
        'powerConnectionHeatingTypeOther',
        new FormControl(this.powerConnectionData?.powerConnectionHeatingTypeOther, [])
      );

      if (this.environmentService.showTechnicianDataEarthWorkArea) {
        this.earthworkAreaTypes = this.commonService.getDropdownData(this.earthworkAreaKey);
        this.powerConnectionFG.addControl('earthworkAreaName', new FormControl(this.powerConnectionData?.earthworkAreaName));
        this.powerConnectionFG.controls.offerEarthworksOnPrivateProperty.valueChanges.subscribe(value => {
          if (value) {
            this.powerConnectionFG.controls.earthworkAreaName.setValidators([Validators.required]);
          } else {
            this.powerConnectionFG.controls.earthworkAreaName.setValue('');
            this.powerConnectionFG.controls.earthworkAreaName.clearValidators();
          }
          this.powerConnectionFG.controls.earthworkAreaName.updateValueAndValidity();
        });
      }

      this.powerConnectionFG.controls.powerConnectionHeatingType.valueChanges.subscribe(value => {
        if (value.toLowerCase() !== 'other') {
          this.powerConnectionFG.controls.powerConnectionHeatingTypeOther.setValue('');
          this.powerConnectionFG.controls.powerConnectionHeatingTypeOther.clearValidators();
        } else {
          this.powerConnectionFG.controls.powerConnectionHeatingTypeOther.setValidators([Validators.required]);
        }
        this.powerConnectionFG.controls.powerConnectionHeatingTypeOther.updateValueAndValidity();
      });
    }

    if (this.showTempConnectionRegistrationForNewMains) {
      const bdewId = this.powerConnectionData?.technicianBdewId ?? this.technicianBdewId;
      this.powerConnectionFG.addControl(
        'requiredTempConnectionLess30',
        new FormControl({ value: this.powerConnectionData?.requiredTempConnectionLess30 ?? false, disabled: this.isReadOnly }, [])
      );
      this.powerConnectionFG.addControl('technicianBdewId', new FormControl(bdewId, []));
      this.powerConnectionFG.addControl('isTechnicianRegistered', new FormControl(!!bdewId, []));
      if (bdewId) {
        this.loadTechnicianByBdewId(bdewId);
        this.addTempPowerReportConfirmedControl(
          this.powerConnectionFG.controls.requiredTempConnectionLess30.value,
          this.powerConnectionData.tempPowerReportConfirmed
        );
      }

      this.powerConnectionFG.controls.requiredTempConnectionLess30.valueChanges.subscribe(value => {
        if (value) {
          this.powerConnectionFG.controls.technicianBdewId.setValidators([Validators.required]);
          this.powerConnectionFG.controls.technicianBdewId.setValue(this.technicianBdewId);
          this.updateTempPowerReportConfirmed(null);
          this.addTempPowerReportConfirmedControl(true);
        } else {
          this.powerConnectionFG.controls.technicianBdewId.clearValidators();
          this.powerConnectionFG.controls.technicianBdewId.setValue('');
          this.powerConnectionFG.controls.isTechnicianRegistered.clearValidators();
          this.powerConnectionFG.controls.isTechnicianRegistered.updateValueAndValidity();
          this.powerConnectionFG.controls.tempPowerReportConfirmed?.clearValidators();
          this.updateTempPowerReportConfirmed(false);
          this.addTempPowerReportConfirmedControl(false);
        }
        this.powerConnectionFG.controls.technicianBdewId?.updateValueAndValidity();
        this.powerConnectionFG.controls.tempPowerReportConfirmed?.updateValueAndValidity();
      });

      this.preventLeak(this.powerConnectionFG.controls.technicianBdewId.valueChanges)
        .pipe(debounceTime(500))
        .subscribe(value => {
          this.powerConnectionFG.controls.isTechnicianRegistered.setValue(false);
          if (this.powerConnectionFG.controls.requiredTempConnectionLess30.value) {
            this.powerConnectionFG.controls.isTechnicianRegistered.setValidators([Validators.requiredTrue]);
            this.powerConnectionFG.controls.isTechnicianRegistered.updateValueAndValidity();
          }
          if (value) {
            this.loadTechnicianByBdewId(value);
          }
        });
    }

    if (this.environmentService.showOtherCommoditiesForInstallation) {
      this.powerConnectionFG.addControl(
        'otherCommoditiesForInstallationGas',
        new FormControl(this.powerConnectionData.otherCommoditiesForInstallationGas ?? false)
      );
      this.powerConnectionFG.addControl(
        'otherCommoditiesForInstallationHeating',
        new FormControl(this.powerConnectionData.otherCommoditiesForInstallationHeating ?? false)
      );
      this.powerConnectionFG.addControl(
        'otherCommoditiesForInstallationWater',
        new FormControl(this.powerConnectionData.otherCommoditiesForInstallationWater ?? false)
      );
      this.powerConnectionFG.addControl(
        'otherCommoditiesForInstallationOther',
        new FormControl(this.powerConnectionData.otherCommoditiesForInstallationOther ?? false)
      );
      this.powerConnectionFG.addControl(
        'otherCommoditiesForInstallationOtherInput',
        new FormControl(this.powerConnectionData.otherCommoditiesForInstallationOtherInput ?? '')
      );
      this.powerConnectionFG.get('otherCommoditiesForInstallationOther').valueChanges.subscribe(checked => {
        if (!checked) {
          this.powerConnectionFG.get('otherCommoditiesForInstallationOtherInput').setValue('');
          this.powerConnectionFG.get('otherCommoditiesForInstallationOtherInput').clearValidators();
        } else {
          this.powerConnectionFG.get('otherCommoditiesForInstallationOtherInput').setValidators([Validators.required]);
        }
        this.powerConnectionFG.get('otherCommoditiesForInstallationOtherInput').updateValueAndValidity();
      });
    }

    this.emitFormData();
    this.preventLeak(this.powerConnectionFG.valueChanges).subscribe(() => this.emitFormData());
    this.cdr.detectChanges();
  }

  get showPowerConnectionExtendendOptionsForNewMains(): boolean {
    return this.currentFlowType === InstallationFlowType.MainsConnection && this.usePowerConnectionExtendedOptionsForNewMains;
  }

  get showPowerHouseConnectionLocationOptionsForNewMains(): boolean {
    return this.showPowerConnectionExtendendOptionsForNewMains;
  }

  get showTempConnectionRegistrationForNewMains(): boolean {
    return (
      this.currentFlowType === InstallationFlowType.MainsConnection && this.environmentService.showTempConnectionRegistrationForNewMains
    );
  }

  get showTempPowerReportConfirmationForNewMains(): boolean {
    return this.showTempConnectionRegistrationForNewMains && (this.isTechnician || this.userService.isUserModeratorRole());
  }

  get showInfoTextRequiredFusePowerLarger200A(): boolean {
    return this.environmentService.showInfoTextRequiredFusePowerLarger200A;
  }

  get isCustomer(): boolean {
    return this.userService.isUserCustomerRole();
  }

  get isTechnician(): boolean {
    return this.userService.isUserTechnicianRole();
  }

  get technicianBdewId(): string {
    return this.isTechnician ? this.commonService?.technicianData?.bdew_id : '';
  }

  public setTechnicianFullName(technician: TechnicianData) {
    this.technicianName = technician ? `${technician.first_name} ${technician.last_name}` : null;
  }

  updateTempPowerReportConfirmed(value: boolean | null) {
    if (this.powerConnectionFG.get('tempPowerReportConfirmed')) {
      this.powerConnectionFG.controls.tempPowerReportConfirmed?.setValue(value);
    }
  }

  private addTempPowerReportConfirmedControl(addControl: boolean, tempPowerReportConfirmedValue: boolean = false) {
    if (!this.showTempPowerReportConfirmationForNewMains) {
      return;
    }

    if (!addControl) {
      this.powerConnectionFG.removeControl('tempPowerReportConfirmed');
      return;
    }

    const value =
      this.isCreatedStatus && this.isTechnician
        ? tempPowerReportConfirmedValue
        : this.isCreatedStatus || this.isDraftStatus || this.isDraftRejectedStatus
        ? false
        : true;
    const disabled = value || !this.isTechnician;

    this.powerConnectionFG.addControl('tempPowerReportConfirmed', new FormControl({ value, disabled }, [Validators.requiredTrue]));
  }

  private emitFormData(): void {
    const validityChange =
      (this.powerConnectionFG.get('technicianBdewId')?.dirty && this.powerConnectionFG.get('isTechnicianRegistered')?.valid) || false;
    this.technicianDataService.registerForComponentFormUpdate.next({
      component: 'powerConnection',
      formData: this.powerConnectionFG,
    });
    this.localStorageService.setItem('power_connection', this.powerConnectionFG.value);
    this.localStorageService.setItem('power_connection_changed', validityChange.toString());
  }

  private resetData(): void {
    this.powerConnectionData = null;
  }

  private loadTechnicianByBdewId(bdewId: string): void {
    this.setTechnicianFullName(null);
    if (bdewId === this.technicianBdewId) {
      this.setTechnicianFullName(this.commonService?.technicianData);
      this.powerConnectionFG?.controls?.isTechnicianRegistered?.setValue(true);
    } else {
      this.preventLeak(this.communicationService.getData(UrlSegments.TechnicianByBdewIdAndPower.replace(':bdewid', bdewId))).subscribe(
        (response: TechnicianData) => {
          if (response?.technician_id) {
            this.setTechnicianFullName(response);
            this.powerConnectionFG?.controls?.isTechnicianRegistered?.setValue(true);
          }
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    }
  }
}
